import { FunctionComponent, useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import * as XLSX from "xlsx";
import InvestorDeliveryInvoiceService from "services/investor-delivery-invoice-service";
import { setToaster } from "common/toastStore";

interface SelectedSummary {
  customerId: number;
  customerName: string;
  billMonth: string;
  billYear: number;
  recordCount: number;
}

interface InvoiceDetailItem {
  id: number;
  customerId: number;
  referenceNumber: string;
  billMonth: string;
  enabledClassification: boolean;
  investorName: string;
  deliveredDate: string;
  billYear: number;
  isActive: boolean;
  createdDate: string;
  createdBy: string;
  updatedDate: string | null;
  updatedBy: string | null;
}

interface InvoiceDetailResponse {
  data: InvoiceDetailItem[];
  paginationInfo?: {
    pageNumber: number;
    totalPages: number;
    pageSize: number;
    totalCount: number;
    hasPrevious: boolean;
    hasNext: boolean;
    previousPage: number | null;
    nextPage: number | null;
  };
}

interface InvoiceDetailDrillDownProps {
  selectedSummary: SelectedSummary;
  onBack: () => void;
}

const InvoiceDetailDrillDown: FunctionComponent<
  InvoiceDetailDrillDownProps
> = ({ selectedSummary, onBack }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [detailRows, setDetailRows] = useState<InvoiceDetailItem[]>([]);
  const [totalDetailRecords, setTotalDetailRecords] = useState<number>(0);
  const [detailFirst, setDetailFirst] = useState<number>(0);
  const [detailLazyParams, setDetailLazyParams] = useState({
    first: 0,
    rows: 25,
    page: 1,
  });

  useEffect(() => {
    setDetailRows([]);
    setTotalDetailRecords(0);
    setDetailFirst(0);
    setDetailLazyParams({ first: 0, rows: 25, page: 1 });
  }, [
    selectedSummary.customerId,
    selectedSummary.billMonth,
    selectedSummary.billYear,
  ]);

  useEffect(() => {
    const loadDetails = async () => {
      setLoading(true);
      try {
        const response: any =
          await InvestorDeliveryInvoiceService.getInvoiceDetails({
            customerId: selectedSummary.customerId,
            billMonth: selectedSummary.billMonth,
            billYear: selectedSummary.billYear,
            pageNumber: detailLazyParams.page,
            pageSize: detailLazyParams.rows,
          });

        const payload: InvoiceDetailResponse = response?.data || { data: [] };
        setDetailRows(payload.data || []);
        setTotalDetailRecords(payload.paginationInfo?.totalCount || 0);
      } catch (error) {
        console.warn(error);
        setDetailRows([]);
        setTotalDetailRecords(0);
        setToaster({
          severity: "error",
          summary: "Error",
          detail: "Failed to load invoice detail records.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [selectedSummary, detailLazyParams]);

  const onDetailPage = (event: any) => {
    setDetailFirst(event.first);
    setDetailLazyParams({
      first: event.first,
      rows: event.rows,
      page: event.page + 1,
    });
  };

  const formatDateTime = (value: string | null | undefined) => {
    if (!value) {
      return "-";
    }

    return new Date(value).toLocaleString();
  };

  const exportDetailsToExcel = async () => {
    setExporting(true);
    try {
      let pageNumber = 1;
      const pageSize = 500;
      let totalPages = 1;
      const allRows: InvoiceDetailItem[] = [];

      while (pageNumber <= totalPages) {
        const response: any =
          await InvestorDeliveryInvoiceService.getInvoiceDetails({
            customerId: selectedSummary.customerId,
            billMonth: selectedSummary.billMonth,
            billYear: selectedSummary.billYear,
            pageNumber,
            pageSize,
          });

        const payload: InvoiceDetailResponse = response?.data || { data: [] };
        allRows.push(...(payload.data || []));
        totalPages = payload.paginationInfo?.totalPages || 1;
        pageNumber += 1;
      }

      const excelRows = allRows.map((item) => ({
        Id: item.id,
        ReferenceNumber: item.referenceNumber,
        BillMonth: item.billMonth,
        BillYear: item.billYear,
        InvestorName: item.investorName,
        EnabledClassification: item.enabledClassification,
        DeliveredDate: item.deliveredDate,
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "InvoiceDetail");

      const fileName = `InvestorDeliveryInvoice_${selectedSummary.customerName}_${selectedSummary.billMonth}_${selectedSummary.billYear}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      setToaster({
        severity: "success",
        summary: "Success",
        detail: "Export completed.",
      });
    } catch (error) {
      console.warn(error);
      setToaster({
        severity: "error",
        summary: "Error",
        detail: "Failed to export invoice detail records.",
      });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mt-4">
      <div
        className="flex align-items-center mb-3"
        style={{ position: "relative", minHeight: "2.5rem" }}
      >
        <Button
          label="Summary"
          className="p-button-link p-0 flex-shrink-0"
          onClick={onBack}
        />

        <div
          style={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          <h3 className="m-0">
            {selectedSummary.customerName} - {selectedSummary.billMonth}{" "}
            {selectedSummary.billYear}
          </h3>
          <span className="text-600 text-sm">
            Total Count: {totalDetailRecords || selectedSummary.recordCount}
          </span>
        </div>

        <Button
          label={exporting ? "Exporting..." : "Export to Excel"}
          icon="pi pi-file-excel"
          className="p-button-success flex-shrink-0"
          style={{ marginLeft: "auto" }}
          onClick={exportDetailsToExcel}
          disabled={exporting || loading}
        />
      </div>

      <DataTable
        className="shadow-1"
        value={detailRows}
        stripedRows
        responsiveLayout="scroll"
        lazy
        paginator
        first={detailFirst}
        rows={detailLazyParams.rows}
        totalRecords={totalDetailRecords}
        rowsPerPageOptions={[25, 50, 100]}
        onPage={onDetailPage}
        loading={loading}
        emptyMessage="No invoice detail records found."
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      >
        <Column field="id" header="Id" />
        <Column field="referenceNumber" header="Reference Number" />
        <Column field="billMonth" header="Bill Month" />
        <Column field="billYear" header="Bill Year" />
        <Column field="investorName" header="Investor Name" />
        <Column
          field="enabledClassification"
          header="Enabled Classification"
          body={(rowData: InvoiceDetailItem) =>
            rowData.enabledClassification ? "Yes" : "No"
          }
        />
        <Column
          field="deliveredDate"
          header="Delivered Date"
          body={(rowData: InvoiceDetailItem) =>
            formatDateTime(rowData.deliveredDate)
          }
        />
      </DataTable>
    </div>
  );
};

export default InvoiceDetailDrillDown;
